/**
 * The signed-out public feed's allowlist — ONE pure module shared by three places so they can
 * never disagree about what a signed-out visitor may read:
 *
 *  - `functions/api/public/[[path]].ts` at the repo root (the Cloudflare Pages Function holding the org key);
 *  - `vite.config.ts` (the dev proxy standing in for that function);
 *  - `lib/client.ts` / `lib/api.ts` (the browser decides up front whether a signed-out request
 *    can go to the public feed at all, or should show "Sign in to see this").
 *
 * No imports, no DOM, no Workers globals — so it's unit-testable in plain Node.
 *
 * What's on the list is exactly what the signed-out Markets browser + Contest detail views read:
 * the reference catalog (leagues, venues), the contest list/detail, a contest's market
 * definitions and its best-price liquidity. Deliberately NOT here: depth, signals, arbitrages,
 * opportunities, price history, contest state (incl. the batch POST), and anything under
 * `auth/` — those are per-user or paid capabilities and stay behind sign-in.
 */

/** Synthetic error codes for signed-out visitors (never an API verdict on a credential). */
export const SIGN_IN_REQUIRED = "sign_in_required";
export const PUBLIC_UNAVAILABLE = "public_unavailable";

/** One allowlisted route: path pattern, edge-cache TTL (seconds), query params it may carry. */
interface PublicRoute {
  re: RegExp;
  ttl: number;
  query: readonly string[];
}

// One path segment that is an id. Conservative charset (observed ids look like
// `pg-1781860817255-mt3cqr7-12301`; the docs show `ct_abc123`), and never `.`/`..` so a
// crafted id can't walk the upstream path.
export const ID = "[A-Za-z0-9_~:-][A-Za-z0-9_.~:-]*";

export const PUBLIC_ROUTES: readonly PublicRoute[] = [
  { re: /^leagues$/, ttl: 3600, query: ["limit"] },
  { re: /^partners$/, ttl: 3600, query: [] },
  { re: /^contests$/, ttl: 60, query: ["league_id", "status", "q", "limit", "cursor"] },
  { re: new RegExp(`^contests/${ID}$`), ttl: 300, query: [] },
  { re: new RegExp(`^contests/${ID}/markets$`), ttl: 3600, query: [] },
  { re: new RegExp(`^contests/${ID}/liquidity$`), ttl: 30, query: [] },
];

/** A title search is more varied and less shareable — cache it for less time. */
const SEARCH_TTL = 30;
export const MAX_PUBLIC_LIMIT = 200;
const DEFAULT_LIMIT = 50;

export interface PublicRequest {
  /** Normalised path relative to `/flow/v1/` (no leading slash), e.g. `contests/abc/liquidity`. */
  path: string;
  /** Rebuilt query: allowlisted params only, fixed order, `limit` clamped. */
  query: URLSearchParams;
  /** Seconds the edge may cache a successful answer. */
  ttl: number;
}

/**
 * Validate + sanitise a public-feed request. `path` is relative to `/flow/v1/` (a leading
 * slash is tolerated). Returns null for anything off the allowlist.
 *
 * The query is REBUILT, not filtered in place: only the route's own params, in a fixed order,
 * so equivalent requests share one cache entry and nothing unexpected reaches the upstream.
 */
export function resolvePublicRequest(
  path: string,
  search: URLSearchParams | string = "",
): PublicRequest | null {
  const clean = path.replace(/^\/+/, "").replace(/\/+$/, "");
  if (clean.includes("//")) return null;
  const route = PUBLIC_ROUTES.find((r) => r.re.test(clean));
  if (!route) return null;

  const incoming = typeof search === "string" ? new URLSearchParams(search) : search;
  const query = new URLSearchParams();
  for (const name of route.query) {
    const value = incoming.get(name);
    if (value === null || value === "") continue;
    query.set(name, name === "limit" ? String(clampLimit(value)) : value);
  }
  const ttl = query.has("q") ? Math.min(route.ttl, SEARCH_TTL) : route.ttl;
  return { path: clean, query, ttl };
}

function clampLimit(raw: string): number {
  const n = Math.floor(Number(raw));
  if (!Number.isFinite(n) || n < 1) return DEFAULT_LIMIT;
  return Math.min(MAX_PUBLIC_LIMIT, n);
}

/** Same-origin path prefix the public feed is served under (function in prod, Vite in dev). */
export const PUBLIC_PREFIX = "/api/public";

/**
 * Map an absolute Flow API URL (`https://api…/flow/v1/contests?x=1`) to its public-feed
 * equivalent (`/api/public/contests?x=1`), or null when it isn't public-eligible. Used by the
 * browser client for signed-out requests.
 */
export function toPublicUrl(flowUrl: string, method = "GET"): string | null {
  if (method.toUpperCase() !== "GET") return null;
  let u: URL;
  try {
    u = new URL(flowUrl, "http://placeholder.invalid");
  } catch {
    return null;
  }
  const m = /\/flow\/v1\/(.+)$/.exec(u.pathname);
  if (!m) return null;
  let rel: string;
  try {
    // The client encodes ids with encodeURIComponent; validate the DECODED form so the
    // charset check means what it says, then re-encode per segment below.
    rel = m[1].split("/").map(decodeURIComponent).join("/");
  } catch {
    return null;
  }
  const req = resolvePublicRequest(rel, u.searchParams);
  if (!req) return null;
  return buildPath(PUBLIC_PREFIX, req);
}

/** `<prefix>/<path>[?query]` with each path segment re-encoded. */
export function buildPath(prefix: string, req: PublicRequest): string {
  const path = req.path.split("/").map(encodeURIComponent).join("/");
  const qs = req.query.toString();
  return `${prefix.replace(/\/+$/, "")}/${path}${qs ? `?${qs}` : ""}`;
}

/**
 * Is this greenbook APP path (what `apiGet` receives, e.g. `/contests?league_id=…`) readable
 * signed-out? The public app paths map 1:1 onto Flow paths, so the same allowlist answers it.
 */
export function isPublicAppPath(appPath: string): boolean {
  const [pathname, qs] = appPath.split("?");
  let decoded: string;
  try {
    decoded = pathname.split("/").map(decodeURIComponent).join("/");
  } catch {
    return false;
  }
  return resolvePublicRequest(decoded, qs ?? "") !== null;
}
